import {
	addUnit,
	startOf,
	getDiff,
	formatDate,
} from "@sembark-travel/datetime-utils"
import { withOrdinalSuffix } from "@sembark-travel/number-utils"
import {
	TTransportServiceDatewisePricesFieldValue,
	TValidTransportServiceDatewisePricesFieldValue,
} from "./TransportServiceDatewisePricesInputField"
import {
	TTravelActivityDatewisePricesInputFieldValue,
	TValidTravelActivityDatewisePricesInputFieldValue,
	getValidTravelActivityData,
} from "../TravelActivityPrices"
import { Required } from "utility-types"
import * as Validator from "yup"
import { collect } from "../utils"

export function createDatesFromStartDateAndDays(
	from: Date,
	days = 1
): Array<Date> {
	const dates = []
	for (let i = 0; i < days; i++) {
		dates.push(startOf(addUnit(from, i, "days"), "day"))
	}
	return dates
}

export function createBookingDateSelectionOptionsFromStartAndEndDates(
	bookingFrom: Date | string | undefined,
	bookingTo: Date | string | undefined
) {
	const dates: Array<{ id: string; name: string; value: Date }> = []
	if (!bookingTo || !bookingFrom) return dates
	const days = getDiff(bookingTo, bookingFrom, "days")
	for (let i = 0; i <= days; i++) {
		const date = addUnit(bookingFrom, i, "day")
		dates.push({
			id: formatDate(date, "YYYY-MM-DD"),
			name: `${withOrdinalSuffix(i + 1)} Day (${formatDate(
				date,
				"ddd D MMM"
			)})`,
			value: startOf(date, "day"),
		})
	}
	return dates
}

export function getValidTransportServiceData(
	c: TTransportServiceDatewisePricesFieldValue
): {
	data: TValidTransportServiceDatewisePricesFieldValue | undefined
	errors: Array<{ cabIndex: number; reason: string }>
} {
	const { cabs, transport_service, transport_service_location, ...others } = c
	if (!cabs?.length || !transport_service || !transport_service_location) {
		return {
			data: undefined,
			errors: [],
		}
	}
	const invalidCabs: Array<{ cabIndex: number; reason: string }> = []

	const validCabs = (c.cabs || []).filter((cab, index) => {
		const hasCabType = Boolean(cab.cab_type)

		// cabType is missing but price is present
		const hasPrice = cab.date_wise_prices?.some((cab) => {
			const givenPrice = cab?.given_price ?? 0
			const perQuantityPrice = cab?.per_quantity_given_price ?? 0
			return givenPrice > 0 || perQuantityPrice > 0
		})

		if (!hasCabType && hasPrice) {
			invalidCabs.push({
				cabIndex: index,
				reason:
					"Cab type is missing but prices are entered. Please select a cab type.",
			})
			return false
		}
		return Boolean(cab.cab_type && cab.no_of_cabs)
	}) as Array<Required<(typeof cabs)[0], "__id" | "cab_type" | "no_of_cabs">>

	if (!validCabs.length) return { data: undefined, errors: invalidCabs }

	return {
		data: {
			...others,
			transport_service,
			transport_service_location,
			cabs: validCabs,
		},
		errors: invalidCabs,
	}
}

export type TDateswiseTransportsAndActivitiesInputFieldValue = Array<{
	__id: number
	dates?: Array<Date>
	// this is only used when we don't have date range to select from
	no_of_days?: number
	services?: Array<
		| {
				__id: number
				type: "transport_service"
				service: TTransportServiceDatewisePricesFieldValue
		  }
		| {
				__id: number
				type: "travel_activity"
				service: TTravelActivityDatewisePricesInputFieldValue
		  }
	>
}>

export type TValidDateswiseTransportsAndActivitiesInputFieldValue = Array<
	Omit<
		Required<TDateswiseTransportsAndActivitiesInputFieldValue[number], "dates">,
		"services"
	> & {
		services: Array<
			| {
					__id: number
					type: "transport_service"
					service: TValidTransportServiceDatewisePricesFieldValue
			  }
			| {
					__id: number
					type: "travel_activity"
					service: TValidTravelActivityDatewisePricesInputFieldValue
			  }
		>
	}
>

export const validDateswiseTransportsAndActivitiesSchema = Validator.array().of(
	Validator.object().shape({
		dates: Validator.array().min(1, "Please select atleast one day"),
		services: Validator.array()
			.min(1, "Please select atleast one service")
			.of(
				Validator.object().shape({
					type: Validator.string(),
				})
			),
	})
)

export function getValidDateswiseTransportsAndActivitiesData(
	daywise_services: TDateswiseTransportsAndActivitiesInputFieldValue | undefined
): {
	data: TValidDateswiseTransportsAndActivitiesInputFieldValue
	errors: Array<{
		dayIndex: number
		serviceName: string
		cabIndex: number
		reason: string
	}>
} {
	const errors: Array<{
		dayIndex: number
		serviceName: string
		cabIndex: number
		reason: string
	}> = []

	const data = (daywise_services || [])
		.filter((s): s is Required<typeof s, "dates" | "services"> =>
			Boolean(s.dates?.length && s.services?.length)
		)
		.map((s, dayIndex) => ({
			...s,
			services: s.services
				.map((s) => {
					let service:
						| TValidTransportServiceDatewisePricesFieldValue
						| TValidTravelActivityDatewisePricesInputFieldValue
						| undefined = undefined

					let result: {
						data: TValidTransportServiceDatewisePricesFieldValue | undefined
						errors: Array<{
							cabIndex: number
							reason: string
						}>
					}
					switch (s.type) {
						case "transport_service":
							result = getValidTransportServiceData(s.service)
							service = result.data

							if (result.errors.length) {
								result.errors.forEach((err) => {
									errors.push({
										dayIndex,
										serviceName: s.service.transport_service?.name || "",
										cabIndex: err.cabIndex,
										reason: err.reason,
									})
								})
							}
							if (!service) return undefined
							return {
								...s,
								service,
							}
						case "travel_activity":
							service = getValidTravelActivityData(s.service)
							if (!service) return undefined
							return {
								...s,
								service,
							}
						default:
							return undefined
					}
				})
				.filter((s): s is Exclude<typeof s, undefined> => {
					return Boolean(s)
				}),
		}))

	return {
		data,
		errors,
	}
}

export function groupAndNormalizePriceForDateswiseServicesByDate(
	dateswiseServices: TValidDateswiseTransportsAndActivitiesInputFieldValue
) {
	let sortOrder = 0
	const datewiseServices = dateswiseServices.flatMap(
		({ dates, services }, currentIndex) => {
			const groupId = currentIndex + 1
			return dates.map((date, i) => ({
				date,
				groupId,
				services: services.map(({ type, service, ...others }) => {
					sortOrder++
					if (type === "transport_service") {
						const { cabs, comments, ...s } = service
						const cabsWithPrices = cabs.map((cab) => {
							const { date_wise_prices, ...c } = cab
							const {
								price,
								per_quantity_given_price,
								given_price,
								per_quantity_booked_price,
								booked_price,
								currency,
							} = date_wise_prices?.at(i) ||
								date_wise_prices?.at(0) || {
									currency: "INR",
									price: undefined,
									given_price: undefined,
									per_quantity_given_price: undefined,
									booked_price: undefined,
									per_quantity_booked_price: undefined,
								}
							return {
								...c,
								currency,
								calculated_price: price,
								per_quantity_given_price: per_quantity_given_price ?? undefined,
								given_price: given_price ?? undefined,
								booked_price: booked_price ?? undefined,
								per_quantity_booked_price:
									per_quantity_booked_price ?? undefined,
								comments: comments || "",
							}
						})
						return {
							...others,
							type,
							service: {
								...s,
								cabs: cabsWithPrices,
								comments,
								group_id: groupId,
								sort_order: sortOrder,
							},
						}
					}
					const { ticket_tourist_configurations, ...s } = service
					const ticketTouristConfigurationsWithPrices =
						ticket_tourist_configurations.map((config) => {
							const { date_wise_prices, ...c } = config
							const {
								price,
								given_price,
								currency,
								per_quantity_given_price,
								per_quantity_booked_price,
								booked_price,
							} = date_wise_prices?.at(i) ||
								date_wise_prices?.at(0) || {
									currency: "INR",
									calculated_price: 0,
									given_price: undefined,
									booked_price: undefined,
									per_quantity_given_price: undefined,
									per_quantity_booked_price: undefined,
								}
							return {
								...c,
								quantity: config.quantity || 0,
								currency,
								calculated_price: price,
								given_price: given_price ?? undefined,
								per_quantity_given_price: per_quantity_given_price ?? undefined,
								per_quantity_booked_price:
									per_quantity_booked_price ?? undefined,
								booked_price: booked_price ?? undefined,
							}
						})
					return {
						...others,
						type,
						service: {
							...s,
							ticket_tourist_configurations:
								ticketTouristConfigurationsWithPrices,
							group_id: groupId,
							sort_order: sortOrder,
						},
					}
				}),
			}))
		}
	)

	return collect(datewiseServices)
		.groupBy((s) => startOf(s.date, "day").toISOString())
		.toArray()
		.map((data) => ({
			date: data[0].date,
			services: data.flatMap((s) => s.services),
		}))
}
